import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Select,
  TimePicker,
  DatePicker,
  Button,
  Space,
  Divider,
  Switch,
  Typography,
} from 'antd';
import { MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import {
  supabase,
  AddressField,
  toGeoPointWKT,
  parseGeoPoint,
  useAuthUser,
  parseUserRole,
  isFleetOwnerRole,
  fetchSystemSettings,
  parseClientRequestNotes,
  composeClientRequestLegNotes,
} from '@ubus/shared';
import type { ClientPassengerGroup, Job, JobLeg, JobType } from '@ubus/shared';
import type { AddressCoordinates } from '@ubus/shared';
import { useTranslation } from 'react-i18next';
import { localeError } from '../utils/localeModal';

const { Text } = Typography;

export type JobFormRow = Job & { job_legs: JobLeg[] };

interface OrgOption {
  id: string;
  name: string;
}

interface FleetOption {
  id: string;
  name: string;
}

interface LegFormValues {
  pickup_address: string;
  dropoff_address: string;
  pickup_coords?: AddressCoordinates | null;
  dropoff_coords?: AddressCoordinates | null;
  earliest_start?: Dayjs;
  latest_start?: Dayjs;
  service_duration_minutes?: number;
  notes?: string;
}

export interface JobFormProps {
  /** When false, skip org/fleet fetch and form reset (page always mounts active). */
  active?: boolean;
  scheduleDate: Dayjs;
  editingJob: JobFormRow | null;
  lockedFleetOperatorId?: string | null;
  onCancel: () => void;
  onSaved: (jobId: string, scheduleDate: string) => void;
}

const JOB_TYPE_OPTIONS: { value: JobType; labelKey: string; description: string }[] = [
  {
    value: 'school_run',
    labelKey: 'schoolRun',
    description: 'Multi-leg pickup/drop-off (e.g. morning drop + afternoon return)',
  },
  { value: 'tour', labelKey: 'tour', description: 'Multi-leg tour transfers' },
  { value: 'transfer', labelKey: 'transfer', description: 'Point-to-point or multi-leg transfer' },
];

function timeToDb(d: Dayjs | undefined, fallback = '09:00:00'): string {
  return (d ?? dayjs(fallback, 'HH:mm:ss')).format('HH:mm:ss');
}

function dbTimeToDayjs(t: string | undefined): Dayjs | undefined {
  if (!t) return undefined;
  return dayjs(t, 'HH:mm:ss');
}

function coordsFromGeopoint(geopoint: string | null | undefined): AddressCoordinates | null {
  if (!geopoint) return null;
  const pt = parseGeoPoint(geopoint);
  return pt ? { lat: pt.lat, lng: pt.lng } : null;
}

/** Keep {lat,lng} in Form without stringifying through <Input>. */
function CoordsFormValue(_props: {
  value?: AddressCoordinates | null;
  onChange?: (v: AddressCoordinates | null) => void;
}) {
  return null;
}

function asAddressCoords(value: unknown): AddressCoordinates | null {
  if (!value || typeof value !== 'object') return null;
  const lat = (value as { lat?: unknown }).lat;
  const lng = (value as { lng?: unknown }).lng;
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

function LegAddressFields({ name, mapActive }: { name: number; mapActive: boolean }) {
  const { t } = useTranslation();
  const form = Form.useFormInstance();
  const pickupCoords = asAddressCoords(Form.useWatch(['legs', name, 'pickup_coords'], form));
  const dropoffCoords = asAddressCoords(Form.useWatch(['legs', name, 'dropoff_coords'], form));

  return (
    <>
      <Form.Item
        name={[name, 'pickup_address']}
        label={t('jobForm.pickup')}
        rules={[
          { required: true, message: t('jobForm.pickupRequired') },
          {
            validator: async () => {
              const coords = asAddressCoords(form.getFieldValue(['legs', name, 'pickup_coords']));
              if (!coords) {
                return Promise.reject(new Error(t('jobForm.addressPickFromGoogle')));
              }
            },
          },
        ]}
        extra={t('jobForm.addressGoogleHint')}
      >
        <AddressField
          variant="google"
          placeholder={t('jobForm.pickupAddress')}
          coordinates={pickupCoords}
          mapActive={mapActive}
          geocodeOnBlur={false}
          onCoordinatesChange={(coords) => {
            form.setFieldValue(['legs', name, 'pickup_coords'], coords);
          }}
        />
      </Form.Item>
      <Form.Item name={[name, 'pickup_coords']} hidden>
        <CoordsFormValue />
      </Form.Item>
      <Form.Item
        name={[name, 'dropoff_address']}
        label={t('jobForm.dropoff')}
        rules={[
          { required: true, message: t('jobForm.dropoffRequired') },
          {
            validator: async () => {
              const coords = asAddressCoords(form.getFieldValue(['legs', name, 'dropoff_coords']));
              if (!coords) {
                return Promise.reject(new Error(t('jobForm.addressPickFromGoogle')));
              }
            },
          },
        ]}
        extra={t('jobForm.addressGoogleHint')}
      >
        <AddressField
          variant="google"
          placeholder={t('jobForm.dropoffAddress')}
          coordinates={dropoffCoords}
          mapActive={mapActive}
          geocodeOnBlur={false}
          onCoordinatesChange={(coords) => {
            form.setFieldValue(['legs', name, 'dropoff_coords'], coords);
          }}
        />
      </Form.Item>
      <Form.Item name={[name, 'dropoff_coords']} hidden>
        <CoordsFormValue />
      </Form.Item>
    </>
  );
}

export default function JobForm({
  active = true,
  scheduleDate,
  editingJob,
  onCancel,
  onSaved,
  lockedFleetOperatorId = null,
}: JobFormProps) {
  const { t } = useTranslation();
  const authUser = useAuthUser();
  const isFleetCreator = isFleetOwnerRole(parseUserRole(authUser)) || Boolean(lockedFleetOperatorId);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [fleets, setFleets] = useState<FleetOption[]>([]);
  const [standbyCoords, setStandbyCoords] = useState<AddressCoordinates | null>(null);
  const [mapActive, setMapActive] = useState(false);
  const [latestStartOffsetMinutes, setLatestStartOffsetMinutes] = useState(30);
  const [offsetReady, setOffsetReady] = useState(false);
  /** Preserve client-request vehicle/groups meta when editing leg notes. */
  const legClientMetaRef = useRef<
    Map<number, { vehiclePreference: string | null; passengerGroups: ClientPassengerGroup[] }>
  >(new Map());
  const [clientRequestMeta, setClientRequestMeta] = useState<{
    vehiclePreference: string | null;
    passengerGroups: ClientPassengerGroup[];
  } | null>(null);
  const jobType: JobType = Form.useWatch('job_type', form) ?? 'school_run';
  const isStandby = jobType === 'charter_standby';

  const latestFromEarliest = useCallback(
    (earliest: Dayjs | undefined) => {
      const base = earliest ?? dayjs('09:00', 'HH:mm');
      return base.add(latestStartOffsetMinutes, 'minute');
    },
    [latestStartOffsetMinutes]
  );

  useEffect(() => {
    if (active) setMapActive(true);
    else setMapActive(false);
  }, [active]);

  useEffect(() => {
    if (!active) {
      setOffsetReady(false);
      return;
    }
    let cancelled = false;
    setOffsetReady(false);
    void fetchSystemSettings()
      .then((s) => {
        if (cancelled) return;
        setLatestStartOffsetMinutes(s.latest_start_offset_minutes);
        setOffsetReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setLatestStartOffsetMinutes(30);
        setOffsetReady(true);
      });
    return () => {
      cancelled = true;
    };
  }, [active]);

  useEffect(() => {
    if (!active) return;
    void Promise.all([
      supabase.from('organizations').select('id, name').order('name'),
      supabase
        .from('fleet_operators')
        .select('id, name')
        .eq('status', 'active')
        .order('name'),
    ]).then(([orgsRes, fleetsRes]) => {
      setOrgs((orgsRes.data as OrgOption[]) ?? []);
      setFleets((fleetsRes.data as FleetOption[]) ?? []);
    });
  }, [active]);

  useEffect(() => {
    if (!active || !offsetReady) return;

    if (editingJob) {
      const standbyLeg = editingJob.job_legs?.find((l) => l.leg_type === 'standby_block');
      const transferLegs = (editingJob.job_legs ?? [])
        .filter((l) => l.leg_type === 'transfer')
        .sort((a, b) => a.sequence - b.sequence);

      if (standbyLeg) {
        setStandbyCoords(coordsFromGeopoint(standbyLeg.pickup_geopoint));
        legClientMetaRef.current = new Map();
        setClientRequestMeta(null);
        form.setFieldsValue({
          title: editingJob.title,
          schedule_date: dayjs(editingJob.schedule_date, 'YYYY-MM-DD'),
          job_type: editingJob.job_type,
          org_id: editingJob.org_id ?? undefined,
          fleet_operator_id:
            lockedFleetOperatorId || editingJob.fleet_operator_id || undefined,
          passenger_count: editingJob.passenger_count,
          notes: editingJob.notes ?? undefined,
          contact_person: editingJob.contact_person ?? undefined,
          contact_phone: editingJob.contact_phone ?? undefined,
          driver_full_day_escort: Boolean(editingJob.driver_full_day_escort),
          standby_address: standbyLeg.pickup_address,
          standby_start: dbTimeToDayjs(standbyLeg.earliest_start),
          standby_end: dbTimeToDayjs(standbyLeg.end_time ?? undefined),
          standby_notes: standbyLeg.notes ?? undefined,
        });
      } else {
        setStandbyCoords(null);
        const metaByIndex = new Map<
          number,
          { vehiclePreference: string | null; passengerGroups: ClientPassengerGroup[] }
        >();
        let summaryPref: string | null = null;
        const summaryGroups: ClientPassengerGroup[] = [];
        const legsForForm = transferLegs.map((leg, index) => {
          const parsed = parseClientRequestNotes(leg.notes);
          metaByIndex.set(index, {
            vehiclePreference: parsed.vehiclePreference,
            passengerGroups: parsed.passengerGroups,
          });
          if (!summaryPref && parsed.vehiclePreference) {
            summaryPref = parsed.vehiclePreference;
          }
          for (const g of parsed.passengerGroups) {
            summaryGroups.push(g);
          }
          return {
            pickup_address: leg.pickup_address,
            dropoff_address: leg.dropoff_address,
            pickup_coords: coordsFromGeopoint(leg.pickup_geopoint),
            dropoff_coords: coordsFromGeopoint(leg.dropoff_geopoint),
            earliest_start: dbTimeToDayjs(leg.earliest_start),
            latest_start: dbTimeToDayjs(leg.latest_start ?? leg.earliest_start),
            service_duration_minutes: leg.service_duration_minutes,
            notes: parsed.specialNotes ?? undefined,
          };
        });
        legClientMetaRef.current = metaByIndex;
        setClientRequestMeta(
          summaryPref || summaryGroups.length > 0
            ? { vehiclePreference: summaryPref, passengerGroups: summaryGroups }
            : null,
        );
        form.setFieldsValue({
          title: editingJob.title,
          schedule_date: dayjs(editingJob.schedule_date, 'YYYY-MM-DD'),
          job_type: editingJob.job_type,
          org_id: editingJob.org_id ?? undefined,
          fleet_operator_id:
            lockedFleetOperatorId || editingJob.fleet_operator_id || undefined,
          passenger_count: editingJob.passenger_count,
          notes: editingJob.notes ?? undefined,
          contact_person: editingJob.contact_person ?? undefined,
          contact_phone: editingJob.contact_phone ?? undefined,
          driver_full_day_escort: Boolean(editingJob.driver_full_day_escort),
          legs: legsForForm,
        });
      }
    } else {
      setStandbyCoords(null);
      legClientMetaRef.current = new Map();
      setClientRequestMeta(null);
      form.resetFields();
      form.setFieldsValue({
        job_type: 'school_run',
        schedule_date: scheduleDate,
        passenger_count: 25,
        driver_full_day_escort: false,
        fleet_operator_id: lockedFleetOperatorId || undefined,
        standby_start: dayjs('10:30', 'HH:mm'),
        standby_end: dayjs('17:00', 'HH:mm'),
        legs: [
          {
            earliest_start: dayjs('09:00', 'HH:mm'),
            latest_start: latestFromEarliest(dayjs('09:00', 'HH:mm')),
            service_duration_minutes: 20,
            pickup_address: '',
            dropoff_address: '',
          },
        ],
      });
    }
  }, [active, offsetReady, editingJob, form, lockedFleetOperatorId, scheduleDate, latestFromEarliest]);

  const buildLegRows = (values: Record<string, unknown>): Array<Record<string, unknown>> => {
    if (values.job_type === 'charter_standby') {
      const addr = values.standby_address as string;
      if (!standbyCoords) {
        throw new Error(t('jobForm.geocodeStandby'));
      }
      const geopoint = toGeoPointWKT(standbyCoords.lat, standbyCoords.lng);
      const notes = ((values.standby_notes as string | undefined) ?? '').trim() || null;
      return [
        {
          sequence: 1,
          leg_type: 'standby_block',
          pickup_address: addr,
          dropoff_address: addr,
          pickup_geopoint: geopoint,
          dropoff_geopoint: geopoint,
          earliest_start: timeToDb(values.standby_start as Dayjs),
          latest_start: timeToDb(values.standby_start as Dayjs),
          end_time: timeToDb(values.standby_end as Dayjs, '17:00:00'),
          service_duration_minutes: 0,
          notes,
        },
      ];
    }

    const legs = (values.legs as LegFormValues[]) ?? [];
    if (legs.length === 0) {
      throw new Error(t('jobForm.addLeg'));
    }

    return legs.map((leg, index) => {
      if (!leg.pickup_coords || !leg.dropoff_coords) {
        throw new Error(t('jobForm.geocodeLeg', { number: index + 1 }));
      }
      const earliest = timeToDb(leg.earliest_start);
      const latest = timeToDb(leg.latest_start ?? leg.earliest_start, earliest);
      const meta = legClientMetaRef.current.get(index);
      return {
        sequence: index + 1,
        leg_type: 'transfer',
        pickup_address: leg.pickup_address,
        dropoff_address: leg.dropoff_address,
        pickup_geopoint: toGeoPointWKT(leg.pickup_coords.lat, leg.pickup_coords.lng),
        dropoff_geopoint: toGeoPointWKT(leg.dropoff_coords.lat, leg.dropoff_coords.lng),
        earliest_start: earliest,
        latest_start: latest,
        end_time: null,
        service_duration_minutes: leg.service_duration_minutes ?? 0,
        notes: composeClientRequestLegNotes({
          specialNotes: (leg.notes ?? '').trim() || null,
          vehiclePreference: meta?.vehiclePreference ?? null,
          passengerGroups: meta?.passengerGroups ?? [],
        }),
      };
    });
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      const legRows = buildLegRows(values);
      const scheduleDateStr = (values.schedule_date as Dayjs).format('YYYY-MM-DD');
      const notes = ((values.notes as string | undefined) ?? '').trim() || null;
      const contactPerson =
        ((values.contact_person as string | undefined) ?? '').trim() || null;
      const contactPhone =
        ((values.contact_phone as string | undefined) ?? '').trim() || null;
      const jobPayload = {
        org_id: (values.org_id as string | undefined) || null,
        fleet_operator_id:
          lockedFleetOperatorId ||
          (values.fleet_operator_id as string | undefined) ||
          null,
        schedule_date: scheduleDateStr,
        job_type: values.job_type as JobType,
        title: (values.title as string).trim(),
        passenger_count: values.passenger_count as number,
        notes,
        contact_person: contactPerson,
        contact_phone: contactPhone,
        driver_full_day_escort: Boolean(values.driver_full_day_escort),
      };

      let savedId: string;
      if (editingJob) {
        const { error: jobError } = await supabase
          .from('jobs')
          .update({ ...jobPayload, updated_at: new Date().toISOString() })
          .eq('id', editingJob.id);

        if (jobError) throw jobError;

        const { error: delError } = await supabase
          .from('job_legs')
          .delete()
          .eq('job_id', editingJob.id);
        if (delError) throw delError;

        const { error: legsError } = await supabase.from('job_legs').insert(
          legRows.map((leg) => ({ ...leg, job_id: editingJob.id }))
        );
        if (legsError) throw legsError;
        savedId = editingJob.id;
      } else {
        const { data: jobData, error: jobError } = await supabase
          .from('jobs')
          .insert({
            ...jobPayload,
            status: 'unscheduled' as const,
            source: isFleetCreator ? ('fleet' as const) : ('hq' as const),
            acceptance_status: 'n/a' as const,
          })
          .select('id')
          .single();

        if (jobError || !jobData) throw jobError ?? new Error(t('jobForm.createFailed'));

        const { error: legsError } = await supabase.from('job_legs').insert(
          legRows.map((leg) => ({ ...leg, job_id: jobData.id }))
        );
        if (legsError) throw legsError;
        savedId = jobData.id;
      }

      onSaved(savedId, scheduleDateStr);
    } catch (err) {
      const isFormValidation =
        err != null &&
        typeof err === 'object' &&
        'errorFields' in err &&
        Array.isArray((err as { errorFields?: unknown }).errorFields);

      localeError(t, {
        title: isFormValidation ? t('jobForm.requiredFieldsTitle') : t('jobForm.saveFailed'),
        content: isFormValidation
          ? t('jobForm.requiredFieldsMissing')
          : err instanceof Error
            ? err.message
            : t('jobForm.unknownError'),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="admin-job-form">
      <Form
        form={form}
        layout="vertical"
        onValuesChange={(changed) => {
          if (!Array.isArray(changed.legs)) return;
          (changed.legs as Array<Partial<LegFormValues> | undefined>).forEach((legChange, index) => {
            if (!legChange || !('earliest_start' in legChange)) return;
            const earliest = legChange.earliest_start;
            if (!earliest || !dayjs.isDayjs(earliest)) return;
            form.setFieldValue(['legs', index, 'latest_start'], latestFromEarliest(earliest));
          });
        }}
      >
        <Form.Item
          name="title"
          label={t('jobForm.title')}
          rules={[{ required: true, message: t('jobForm.titleRequired') }]}
        >
          <Input placeholder={t('jobForm.titlePlaceholder')} />
        </Form.Item>

        <Form.Item
          name="schedule_date"
          label={t('jobForm.scheduleDate')}
          rules={[{ required: true, message: t('jobForm.scheduleDateRequired') }]}
        >
          <DatePicker style={{ width: '100%' }} allowClear={false} />
        </Form.Item>

        <Form.Item name="job_type" label={t('jobForm.type')} rules={[{ required: true }]}>
          <Select
            disabled={editingJob?.job_type === 'charter_standby'}
            options={[
              ...JOB_TYPE_OPTIONS.map((o) => ({
                value: o.value,
                label: t(`jobForm.${o.labelKey}`),
              })),
              ...(editingJob?.job_type === 'charter_standby'
                ? [{ value: 'charter_standby' as const, label: t('jobForm.standby') }]
                : []),
            ]}
            onChange={(value: JobType) => {
              setStandbyCoords(null);
              if (value === 'tour') {
                form.setFieldValue('driver_full_day_escort', true);
              }
            }}
          />
        </Form.Item>

        {!isStandby && (
          <Form.Item
            name="driver_full_day_escort"
            label={t('jobForm.fullDayEscort')}
            valuePropName="checked"
            extra={t('jobForm.fullDayEscortHint')}
          >
            <Switch />
          </Form.Item>
        )}

        {isStandby && (
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('jobForm.standbyWarning')}
            description={t('jobForm.standbyWarningDescription')}
          />
        )}

        <Space size="large" style={{ display: 'flex' }} align="start" wrap>
          <Form.Item
            name="passenger_count"
            label={t('jobForm.passengers')}
            rules={[{ required: true, type: 'number', min: 1 }]}
          >
            <InputNumber min={1} max={100} style={{ width: 120 }} />
          </Form.Item>
          <Form.Item
            name="org_id"
            label={t('jobForm.customer')}
            style={{ flex: 1, minWidth: 200 }}
          >
            <Select
              allowClear
              placeholder={t('jobForm.selectOrganization')}
              options={orgs.map((o) => ({ value: o.id, label: o.name }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
          <Form.Item
            name="fleet_operator_id"
            label={t('jobForm.fleet')}
            extra={
              lockedFleetOperatorId ? undefined : t('jobForm.fleetOptional')
            }
            style={{ flex: 1, minWidth: 200 }}
            hidden={Boolean(lockedFleetOperatorId)}
          >
            <Select
              allowClear={!lockedFleetOperatorId}
              disabled={Boolean(lockedFleetOperatorId)}
              placeholder={t('jobForm.selectFleet')}
              options={fleets.map((f) => ({ value: f.id, label: f.name }))}
              showSearch
              optionFilterProp="label"
            />
          </Form.Item>
        </Space>

        <Form.Item name="notes" label={t('jobForm.notes')}>
          <Input.TextArea rows={2} placeholder={t('jobForm.notesPlaceholder')} />
        </Form.Item>

        {clientRequestMeta ? (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={t('jobForm.clientRequestMetaTitle')}
            description={
              <Space direction="vertical" size={4} style={{ width: '100%' }}>
                {clientRequestMeta.vehiclePreference ? (
                  <Text>
                    {t('jobForm.clientVehiclePreference')}:{' '}
                    {{
                      standard: t('jobForm.vehiclePrefStandard'),
                      mini: t('jobForm.vehiclePrefMini'),
                      coach: t('jobForm.vehiclePrefCoach'),
                      any: t('jobForm.vehiclePrefAny'),
                    }[clientRequestMeta.vehiclePreference] ?? clientRequestMeta.vehiclePreference}
                  </Text>
                ) : null}
                {clientRequestMeta.passengerGroups.length > 0 ? (
                  <div>
                    <Text>{t('jobForm.clientPassengerGroups')}:</Text>
                    <ul style={{ margin: '4px 0 0', paddingLeft: 20 }}>
                      {clientRequestMeta.passengerGroups.map((g, i) => (
                        <li key={`${g.name}-${i}`}>
                          {g.name} · {g.count}
                          {g.lead ? ` · ${g.lead}` : ''}
                          {g.phone ? ` · ${g.phone}` : ''}
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : null}
              </Space>
            }
          />
        ) : null}

        <Space size="large" style={{ display: 'flex' }} align="start" wrap>
          <Form.Item
            name="contact_person"
            label={t('jobForm.contactPerson')}
            style={{ flex: 1, minWidth: 180 }}
            rules={[{ required: true, whitespace: true, message: t('jobForm.contactPersonRequired') }]}
          >
            <Input placeholder={t('jobForm.contactPersonPlaceholder')} allowClear />
          </Form.Item>
          <Form.Item
            name="contact_phone"
            label={t('jobForm.contactPhone')}
            style={{ flex: 1, minWidth: 180 }}
            rules={[{ required: true, whitespace: true, message: t('jobForm.contactPhoneRequired') }]}
          >
            <Input placeholder={t('jobForm.contactPhonePlaceholder')} allowClear />
          </Form.Item>
        </Space>

        {isStandby ? (
          <>
            <Form.Item
              name="standby_address"
              label={t('jobForm.standbyLocation')}
              rules={[
                { required: true, message: t('jobForm.standbyAddressRequired') },
                {
                  validator: async () => {
                    if (!standbyCoords?.lat || !standbyCoords?.lng) {
                      return Promise.reject(new Error(t('jobForm.addressPickFromGoogle')));
                    }
                  },
                },
              ]}
              extra={t('jobForm.addressGoogleHint')}
            >
              <AddressField
                variant="google"
                placeholder={t('jobForm.standbyAddress')}
                coordinates={standbyCoords}
                mapActive={mapActive}
                geocodeOnBlur={false}
                onCoordinatesChange={setStandbyCoords}
              />
            </Form.Item>
            <Space size="large">
              <Form.Item
                name="standby_start"
                label={t('jobForm.standbyStarts')}
                rules={[{ required: true, message: t('jobForm.startRequired') }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
              <Form.Item
                name="standby_end"
                label={t('jobForm.standbyEnds')}
                rules={[{ required: true, message: t('jobForm.endRequired') }]}
              >
                <TimePicker format="HH:mm" minuteStep={5} />
              </Form.Item>
            </Space>
            <Form.Item name="standby_notes" label={t('jobForm.legNotes')}>
              <Input.TextArea rows={2} placeholder={t('jobForm.legNotesPlaceholder')} />
            </Form.Item>
          </>
        ) : (
          <>
            <Divider orientation="left" plain>
              {t('jobForm.legs')}
            </Divider>
            <Form.List name="legs">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <div
                      key={key}
                      style={{
                        marginBottom: 16,
                        padding: 12,
                        border: '1px solid var(--color-border, #f0f0f0)',
                        borderRadius: 8,
                      }}
                    >
                      <Space align="baseline" style={{ marginBottom: 8 }}>
                        <strong>{t('jobForm.leg', { number: name + 1 })}</strong>
                        {fields.length > 1 && (
                          <Button
                            type="text"
                            danger
                            icon={<MinusCircleOutlined />}
                            onClick={() => remove(name)}
                          >
                            {t('jobForm.remove')}
                          </Button>
                        )}
                      </Space>
                      <Space size="middle" wrap style={{ marginBottom: 8 }}>
                        <Form.Item
                          {...rest}
                          name={[name, 'earliest_start']}
                          label={t('jobForm.startBy')}
                          rules={[{ required: true }]}
                        >
                          <TimePicker format="HH:mm" minuteStep={5} />
                        </Form.Item>
                        <Form.Item {...rest} name={[name, 'latest_start']} label={t('jobForm.latestStart')}>
                          <TimePicker format="HH:mm" minuteStep={5} />
                        </Form.Item>
                        <Form.Item
                          {...rest}
                          name={[name, 'service_duration_minutes']}
                          label={t('jobForm.serviceMinutes')}
                          rules={[{ required: true }]}
                        >
                          <InputNumber min={0} max={480} step={5} style={{ width: 100 }} />
                        </Form.Item>
                      </Space>
                      <LegAddressFields name={name} mapActive={mapActive} />
                      <Form.Item {...rest} name={[name, 'notes']} label={t('jobForm.legNotes')}>
                        <Input.TextArea rows={2} placeholder={t('jobForm.legNotesPlaceholder')} />
                      </Form.Item>
                    </div>
                  ))}
                  <Button
                    type="dashed"
                    onClick={() => {
                      const earliest = dayjs('09:00', 'HH:mm');
                      add({
                        earliest_start: earliest,
                        latest_start: latestFromEarliest(earliest),
                        service_duration_minutes: 30,
                        pickup_address: '',
                        dropoff_address: '',
                      });
                    }}
                    block
                    icon={<PlusOutlined />}
                  >
                    {t('jobForm.add')}
                  </Button>
                </>
              )}
            </Form.List>
          </>
        )}
      </Form>
      <Space style={{ marginTop: 24 }}>
        <Button onClick={onCancel}>{t('common.cancel')}</Button>
        <Button type="primary" loading={saving} onClick={() => void handleSave()}>
          {t('jobForm.save')}
        </Button>
      </Space>
    </div>
  );
}
